import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { POLL_OPTIONS } from "../../lib/constants";
import { BarChart2, CheckCircle, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const LivePollSection = () => {
  const [votes, setVotes] = useState({
    caramel: 127,
    chocolate: 89,
    white: 45,
    mix: 156,
  });
  const [hasVoted, setHasVoted] = useState(false);
  const [userVote, setUserVote] = useState(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  // Simulate live updates
  useEffect(() => {
    if (!hasVoted) return;
    
    const interval = setInterval(() => {
      const randomOption = POLL_OPTIONS[Math.floor(Math.random() * POLL_OPTIONS.length)];
      setVotes((prev) => ({
        ...prev,
        [randomOption.id]: prev[randomOption.id] + 1,
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, [hasVoted]);

  const totalVotes = Object.values(votes).reduce((sum, v) => sum + v, 0);

  const getPercentage = (optionId) => {
    return Math.round((votes[optionId] / totalVotes) * 100);
  };

  const handleVote = (optionId) => {
    if (hasVoted) return;

    setVotes((prev) => ({
      ...prev,
      [optionId]: prev[optionId] + 1,
    }));
    setUserVote(optionId);
    setHasVoted(true);

    // Celebration
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.7 },
      colors: ["#C88037", "#2D1810"],
    });

    toast.success("¡Gracias por votar!");
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Ingresa tu email");
      return;
    }
    
    // Here you would send to backend
    setEmailSubmitted(true);
    toast.success("¡Te avisaremos cuando salga el nuevo sabor!");
    
    confetti({
      particleCount: 30,
      spread: 50,
      origin: { y: 0.8 },
      colors: ["#C88037", "#25D366"],
    });
  };

  return (
    <section
      id="encuesta"
      className="py-20 md:py-32 bg-[#FDFBF7]"
      data-testid="poll-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider flex items-center justify-center gap-2">
            <BarChart2 className="w-4 h-4" strokeWidth={1.5} />
            Encuesta en Vivo
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D1810] mt-2 mb-4">
            ¿Cuál es tu <span className="italic text-[#C88037]">team</span>?
          </h2>
          <p className="text-[#5E4B3C] max-w-xl mx-auto">
            Estamos creando un sabor nuevo y queremos tu opinión. 
            ¡Vota y podrías recibir un cupón sorpresa cuando salga!
          </p>
        </motion.div>

        {/* Poll */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#F4EDE4] rounded-3xl p-6 md:p-10"
        >
          <div className="space-y-4">
            {POLL_OPTIONS.map((option, index) => {
              const percentage = getPercentage(option.id);
              const isSelected = userVote === option.id;

              return (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: hasVoted ? 1 : 1.01 }}
                  whileTap={{ scale: hasVoted ? 1 : 0.99 }}
                  onClick={() => handleVote(option.id)}
                  disabled={hasVoted}
                  className={`w-full relative overflow-hidden rounded-xl transition-all ${
                    hasVoted
                      ? "cursor-default"
                      : "cursor-pointer hover:shadow-md"
                  } ${
                    isSelected
                      ? "ring-2 ring-[#C88037] ring-offset-2"
                      : ""
                  }`}
                  data-testid={`poll-option-${option.id}`}
                >
                  <div className="relative z-10 flex items-center justify-between p-4 bg-white/80">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{option.emoji}</span>
                      <span className="font-medium text-[#2D1810]">{option.text}</span>
                      {isSelected && (
                        <CheckCircle className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                      )}
                    </div>
                    {hasVoted && (
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#C88037]">{percentage}%</span>
                        <span className="text-[#5E4B3C] text-sm">({votes[option.id]})</span>
                      </div>
                    )}
                  </div>

                  {/* Progress bar */}
                  {hasVoted && (
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                      className="absolute inset-0 bg-[#C88037]/20 z-0"
                    />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Total votes */}
          <div className="text-center mt-6 text-[#5E4B3C] text-sm">
            {totalVotes} votos en total • Se actualiza en tiempo real
          </div>

          {/* Email CTA */}
          {hasVoted && !emailSubmitted && (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleEmailSubmit}
              className="mt-8 pt-8 border-t border-[#E5DCD0]"
            >
              <div className="flex items-center gap-2 text-[#C88037] justify-center mb-4">
                <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">¿Quieres un cupón cuando salga el nuevo sabor?</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Tu email"
                  className="flex-1 px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                  data-testid="poll-email-input"
                />
                <button
                  type="submit"
                  className="bg-[#C88037] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#E5984A] transition-colors flex items-center justify-center gap-2"
                  data-testid="poll-email-submit"
                >
                  <Mail className="w-5 h-5" strokeWidth={1.5} />
                  Avisarme
                </button>
              </div>
            </motion.form>
          )}

          {emailSubmitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-8 pt-8 border-t border-[#E5DCD0] text-center"
            >
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">¡Listo! Te avisaremos pronto</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default LivePollSection;
